import * as d3 from 'd3'
import { motion } from 'motion/react'
import * as React from 'react'

import { LabeledSwatch } from '@/components/ui/LabeledSwatch'
import { cn, parseCalendarDate } from '@/utils'
import { ChartControls } from './Controls'
import { ChartGrid } from './Grid'
import { useChartBase, useChartInteraction } from './Hooks'
import { ChartLegend } from './Legend'
import { ChartTooltip } from './Tooltip'
import type { ChartExportProps, MultiSeriesDataPoint } from './types'
import { currencyTickFormatter, dateTickFormatter } from './utils/formatters'

interface SeriesConfig {
  key: string
  label: string
  color: string
  area?: boolean
}

interface LineData extends SeriesConfig {
  path: string | null
  areaPath?: string | null
  data: Array<{
    dateObj: Date
    value: number
    x: number
    y: number
  }>
}

interface MultiLineTooltipContentProps {
  hoveredData: MultiSeriesDataPoint | null
  lines: LineData[]
  formatter?: (value: number) => string
}

const MultiLineTooltipContent = React.memo<MultiLineTooltipContentProps>(
  ({ hoveredData, lines, formatter }) => {
    if (!hoveredData || !hoveredData.dateObj) return null

    const sortedLines = [...lines]
      .map((line) => ({
        ...line,
        value: hoveredData[line.key] ?? 0,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)

    return (
      <div className="space-y-1">
        <div className="text-xs text-muted-foreground">
          {hoveredData.dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
        {sortedLines.map((line, i) => (
          <LabeledSwatch
            key={line.key || i}
            color={line.color}
            className="gap-2 text-sm"
            label={
              <span
                className="max-w-30 truncate font-medium"
                title={line.label}
              >
                {line.label}:
              </span>
            }
            value={
              formatter
                ? formatter(line.value)
                : `$${line.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            }
          />
        ))}
        {lines.length > 5 && (
          <div className="text-xs text-muted-foreground pt-1 border-t">
            +{lines.length - 5} more
          </div>
        )}
      </div>
    )
  },
)

interface MultiLineChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChartExportProps {
  data?: MultiSeriesDataPoint[]
  series?: SeriesConfig[]
  className?: string
  height?: number
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  strokeWidth?: number
  showDots?: boolean
  showLegend?: boolean
  legendPosition?: 'right' | 'bottom' | 'top'
  interactiveLegend?: boolean
  onChartReady?: (params: {
    containerRef: React.RefObject<HTMLDivElement>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  renderTooltip?: (
    hoveredData: MultiSeriesDataPoint | null,
    lines: LineData[],
  ) => React.ReactNode
  tooltipFormatter?: (value: number) => string
  showHoverControls?: boolean
  // 'zero' (default) anchors the y-axis at 0, the right call for volumes/counts. 'fit' frames the
  // axis to the data range so a series that moves within a narrow band (e.g. an $8M portfolio that
  // shifts by a few percent) shows its real change instead of reading as a flat line.
  yDomainMode?: 'zero' | 'fit'
  // When true, the x-axis ticks land on the actual data dates instead of d3's auto-chosen intervals.
  // Useful for a sparse monthly series, where auto-ticks would repeat the same month label.
  xTicksFromData?: boolean
}

const MultiLineChartComponent: React.FC<MultiLineChartProps> = ({
  data = [],
  series = [],
  className,
  height = 400,
  marginTop = 30,
  marginRight = 30,
  marginBottom = 60,
  marginLeft = 70,
  strokeWidth = 2,
  showDots = true,
  showLegend = true,
  legendPosition = 'right',
  interactiveLegend = true,
  onChartReady,
  renderTooltip,
  tooltipFormatter,
  projectName,
  title,
  filters,
  schema,
  dateRange,
  filename = 'chart-export.png',
  showHoverControls = false,
  yDomainMode = 'zero',
  xTicksFromData = false,
  ...props
}) => {
  const {
    containerRef,
    dimensions,
    isFullscreen,
    toggleFullscreen,
    fullscreenError,
    fullscreenSupported,
  } = useChartBase(onChartReady)
  const [visibleSeries, setVisibleSeries] = React.useState<
    Record<string, boolean>
  >(() =>
    series.reduce(
      (acc, s) => ({ ...acc, [s.key]: true }),
      {} as Record<string, boolean>,
    ),
  )

  const adjustedMarginRight = React.useMemo(() => {
    return showLegend && legendPosition === 'right'
      ? Math.max(marginRight, 220)
      : marginRight
  }, [marginRight, showLegend, legendPosition])

  const adjustedMarginTop = React.useMemo(() => {
    return showLegend && legendPosition === 'top'
      ? Math.max(marginTop, marginTop + 40)
      : marginTop
  }, [marginTop, showLegend, legendPosition])

  const adjustedMarginBottom = React.useMemo(() => {
    return showLegend && legendPosition === 'bottom'
      ? Math.max(marginBottom, marginBottom + 40)
      : marginBottom
  }, [marginBottom, showLegend, legendPosition])

  const processedData = React.useMemo<MultiSeriesDataPoint[]>(() => {
    return data.map((d) => ({ ...d, dateObj: parseCalendarDate(d.date) }))
  }, [data])

  React.useEffect(() => {
    // Reset every series to visible when the set of series changes. Key on
    // the series themselves, but bail out (return the same object) when the
    // keys are unchanged — otherwise a caller that rebuilds an equivalent
    // `series` array on every render drives an endless setState -> re-render
    // loop ("Maximum update depth exceeded").
    setVisibleSeries((previousVisibility) => {
      const sameKeys =
        Object.keys(previousVisibility).length === series.length &&
        series.every((seriesItem) => seriesItem.key in previousVisibility)
      if (sameKeys) return previousVisibility
      return series.reduce(
        (visibility, seriesItem) => ({ ...visibility, [seriesItem.key]: true }),
        {} as Record<string, boolean>,
      )
    })
  }, [series])

  const handleToggleSeries = React.useCallback((key: string) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const visibleSeriesData = React.useMemo<SeriesConfig[]>(() => {
    return series.filter((s) => visibleSeries[s.key])
  }, [series, visibleSeries])

  const { xScale, yScale, lines } = React.useMemo<{
    xScale?: d3.ScaleTime<number, number>
    yScale?: d3.ScaleLinear<number, number>
    lines?: LineData[]
  }>(() => {
    if (!dimensions.width || !dimensions.height || !processedData.length) {
      return {}
    }

    const x = d3
      .scaleTime()
      .domain(d3.extent(processedData, (d) => d.dateObj) as [Date, Date])
      .range([marginLeft, dimensions.width - adjustedMarginRight])

    const allValues = processedData.flatMap((d) =>
      visibleSeriesData.map((s) => {
        const value = Number(d[s.key])
        return Number.isNaN(value) ? 0 : value
      }),
    )
    // Anchor zero in the domain and include the min so negative series
    // values (e.g. net cash flow with shortfall months) stay inside the
    // frame instead of drawing below it.
    const maxValue = d3.max(allValues) ?? 0
    const minValue = d3.min(allValues) ?? 0

    // Pad the range so the line doesn't touch the top/bottom edges; for a flat series, pad around
    // the value. Falls back to a zero baseline when the data isn't strictly positive (mixed signs
    // read clearest from 0).
    const yDomain = ((): [number, number] => {
      if (yDomainMode !== 'fit' || minValue <= 0) return [0, maxValue]
      if (maxValue === minValue) return [minValue * 0.95, maxValue * 1.05]
      const padding = (maxValue - minValue) * 0.15
      return [minValue - padding, maxValue + padding]
    })()

    const y = d3
      .scaleLinear()
      .domain(yDomain)
      .nice()
      .range([dimensions.height - adjustedMarginBottom, adjustedMarginTop])

    interface ProcessedPoint {
      dateObj: Date
      value: number | null
    }

    const lineGenerator = d3
      .line<ProcessedPoint>()
      .x((d) => x(d.dateObj))
      .y((d) => {
        const value = Number(d.value)
        return y(Number.isNaN(value) ? 0 : value)
      })
      .curve(d3.curveMonotoneX)
      .defined(
        (d) =>
          d.value !== null &&
          d.value !== undefined &&
          !Number.isNaN(Number(d.value)),
      )

    const areaGenerator = d3
      .area<ProcessedPoint>()
      .x((d) => x(d.dateObj))
      .y0(dimensions.height - adjustedMarginBottom)
      .y1((d) => {
        const value = Number(d.value)
        return y(Number.isNaN(value) ? 0 : value)
      })
      .curve(d3.curveMonotoneX)
      .defined(
        (d) =>
          d.value !== null &&
          d.value !== undefined &&
          !Number.isNaN(Number(d.value)),
      )

    const generatedLines: LineData[] = visibleSeriesData.map((s) => {
      const dataWithNulls: ProcessedPoint[] = processedData.map((d) => {
        const value = Number(d[s.key])
        return {
          dateObj: d.dateObj!,
          value: Number.isNaN(value) ? null : value,
        }
      })

      let consecutiveMissing = 0
      const dataWithGaps = dataWithNulls.map((d) => {
        if (d.value === null || d.value === 0) {
          consecutiveMissing++
          if (consecutiveMissing > 2) {
            return { ...d, value: null }
          }
        } else {
          consecutiveMissing = 0
        }
        return d
      })

      return {
        ...s,
        path: lineGenerator(dataWithGaps),
        areaPath: s.area ? areaGenerator(dataWithGaps) : undefined,
        data: processedData.map((d) => {
          const value = Number(d[s.key])
          const safeValue = Number.isNaN(value) ? 0 : value
          return {
            dateObj: d.dateObj!,
            value: safeValue,
            x: x(d.dateObj!),
            y: y(safeValue),
          }
        }),
      }
    })

    return { xScale: x, yScale: y, lines: generatedLines }
  }, [
    processedData,
    dimensions,
    adjustedMarginTop,
    adjustedMarginRight,
    adjustedMarginBottom,
    marginLeft,
    visibleSeriesData,
    yDomainMode,
  ])

  const { hoveredData, handlePointerMove, handlePointerLeave } =
    useChartInteraction(xScale, processedData)

  const dataFingerprint = React.useMemo(() => {
    if (!processedData.length) return 'empty'
    const first = processedData[0]
    const last = processedData[processedData.length - 1]
    return `${processedData.length}-${first.dateObj?.getTime() ?? 0}-${last.dateObj?.getTime() ?? 0}`
  }, [processedData])

  const tooltipPosition = React.useMemo<
    { x: number; y: number } | undefined
  >(() => {
    if (!hoveredData || !xScale || !yScale || !lines || !hoveredData.dateObj)
      return undefined
    const yValues = lines.map((line) => yScale(hoveredData[line.key] ?? 0))
    const topY = Math.min(...yValues)
    return {
      x: xScale(hoveredData.dateObj),
      y: topY,
    }
  }, [hoveredData, xScale, yScale, lines])

  const tooltipAlign = React.useMemo<'start' | 'center' | 'end'>(() => {
    const index = (hoveredData as { index?: number } | null)?.index
    if (index === undefined) return 'center'
    if (index === 0) return 'start'
    if (index === processedData.length - 1) return 'end'
    return 'center'
  }, [hoveredData, processedData.length])

  if (!processedData.length) {
    return (
      <div
        className={cn(
          'w-full h-full flex items-center justify-center',
          className,
        )}
      >
        <p className="text-muted-foreground text-sm">No data to display</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full h-full relative group',
        isFullscreen && 'bg-white',
        className,
      )}
      {...props}
    >
      {dimensions.width > 0 &&
        dimensions.height > 0 &&
        xScale &&
        yScale &&
        lines && (
          <svg
            role="img"
            aria-label="Multi line chart"
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            className="overflow-visible"
          >
            <ChartGrid
              dimensions={dimensions}
              xScale={xScale}
              yScale={yScale}
              margin={{
                left: marginLeft,
                right: adjustedMarginRight,
                top: adjustedMarginTop,
                bottom: adjustedMarginBottom,
              }}
              xTickFormatter={dateTickFormatter}
              yTickFormatter={currencyTickFormatter}
              xTickValues={
                xTicksFromData && processedData.length <= 8
                  ? processedData.map((point) => point.dateObj)
                  : undefined
              }
              fontSize={isFullscreen ? 14 : 10}
            />

            {lines.map((line, i) => (
              <g key={`${line.key || i}-${dataFingerprint}`}>
                {line.areaPath && (
                  <motion.path
                    d={line.areaPath}
                    fill={line.color}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.15 }}
                    transition={{
                      duration: 0.6,
                      ease: 'easeOut',
                      delay: i * 0.05,
                    }}
                  />
                )}

                <motion.path
                  d={line.path ?? undefined}
                  fill="none"
                  stroke={line.color}
                  strokeWidth={strokeWidth}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: 0.8,
                    ease: 'easeOut',
                    delay: i * 0.05,
                  }}
                />

                {showDots &&
                  line.data.map(
                    (
                      point: {
                        x: number
                        y: number
                        value: number
                        dateObj: Date
                      },
                      j: number,
                    ) => (
                      <motion.circle
                        key={j}
                        cx={point.x}
                        cy={point.y}
                        r={
                          hoveredData && (hoveredData as any).index === j
                            ? 5
                            : 3
                        }
                        fill={line.color}
                        stroke="white"
                        strokeWidth={
                          hoveredData && (hoveredData as any).index === j
                            ? 2
                            : 1
                        }
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2, delay: 0.5 + j * 0.015 }}
                      />
                    ),
                  )}
              </g>
            ))}

            <rect
              x={marginLeft}
              y={adjustedMarginTop}
              width={dimensions.width - marginLeft - adjustedMarginRight}
              height={
                dimensions.height - adjustedMarginTop - adjustedMarginBottom
              }
              fill="transparent"
              onPointerMove={handlePointerMove}
              onPointerLeave={handlePointerLeave}
            />

            {hoveredData && xScale && hoveredData.dateObj && (
              <line
                x1={xScale(hoveredData.dateObj)}
                y1={adjustedMarginTop}
                x2={xScale(hoveredData.dateObj)}
                y2={dimensions.height - adjustedMarginBottom}
                className="stroke-border"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            )}
          </svg>
        )}

      {showLegend && (
        <ChartLegend
          items={series.map((s) => ({
            key: s.key,
            label: s.label,
            color: s.color,
            visible: visibleSeries[s.key],
          }))}
          onToggle={interactiveLegend ? handleToggleSeries : undefined}
          position={legendPosition}
          interactive={interactiveLegend}
          chartMargins={{
            top: adjustedMarginTop,
            bottom: adjustedMarginBottom,
          }}
        />
      )}

      {showHoverControls && (
        <ChartControls
          containerRef={containerRef}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          fullscreenError={fullscreenError}
          fullscreenSupported={fullscreenSupported}
          filename={filename}
          projectName={projectName}
          title={title}
          filters={filters}
          schema={schema}
          dateRange={dateRange}
        />
      )}

      <ChartTooltip
        active={!!hoveredData}
        position={tooltipPosition}
        align={tooltipAlign}
      >
        {renderTooltip ? (
          renderTooltip(hoveredData, lines ?? [])
        ) : (
          <MultiLineTooltipContent
            hoveredData={hoveredData}
            lines={lines ?? []}
            formatter={tooltipFormatter}
          />
        )}
      </ChartTooltip>
    </div>
  )
}
MultiLineChartComponent.displayName = 'MultiLineChart'

const MultiLineChart = React.memo(MultiLineChartComponent)

export { MultiLineChart }
